import type { ISbStoryData } from "storyblok-js-client";

export const resolveId = (id: string | ISbStoryData<unknown> | undefined) => {
  if (!id) return;
  if (typeof id === "string") return id;
  return id.uuid;
};

export interface AssetStoryblok {
  alt?: string | null;
  copyright?: string | null;
  id?: number;
  filename?: string | null;
  name?: string;
  title?: string | null;
  focus?: string | null;
  [k: string]: unknown;
}

const imageExtensions = [".gif", ".jpg", ".jpeg", ".png", ".webp", ".svg"];
const videoExtensions = [
  ".mpg",
  ".mp2",
  ".mpeg",
  ".mpe",
  ".mpv",
  ".mp4",
  ".webm",
  ".mov",
  ".avi",
];

const hasExtension = (pathname: string, extensions: string[]) =>
  extensions.some((ext) => pathname.toLowerCase().endsWith(ext));

export const getStoryblokFocus = ({
  width,
  height,
  focus,
}: {
  focus?: string | null;
  width: number;
  height: number;
}) => {
  if (!focus) return "center";

  const xInt = parseInt(focus?.split(":")[0]?.split("x")[0] ?? "0");
  const yInt = parseInt(focus?.split(":")[0]?.split("x")[1] ?? "0");

  const x = Math.round((xInt / width) * 100);
  const y = Math.round((yInt / height) * 100);

  return `${x}% ${y}%`;
};

export const getMedia = (blok: AssetStoryblok) => {
  if (!blok?.filename) return null;

  try {
    const url = new URL(blok.filename);
    const pathname = url.pathname;

    if (hasExtension(pathname, imageExtensions)) {
      const resolution = pathname.split("/")[3];
      const [widthStr, heightStr] = resolution?.split("x") ?? [];

      const width = parseInt(widthStr || "0");
      const height = parseInt(heightStr || "0");

      const objectPosition =
        width && height
          ? getStoryblokFocus({
              width,
              height,
              focus: blok.focus,
            })
          : undefined;

      return {
        url: url.toString(),
        alt: blok.alt,
        width,
        height,
        type: "image" as const,
        objectPosition,
      };
    }

    if (hasExtension(pathname, videoExtensions)) {
      return {
        url: url.toString(),
        alt: blok.alt,
        width: 0,
        height: 0,
        type: "video" as const,
      };
    }
  } catch (error) {
    console.error("getMedia error:", error);
  }

  return null;
};
